import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { ContactFormConfig, ContactFormFrontendProps } from '../../types/ContactForm';
import { useCaptcha } from '../../hooks/useCaptcha';
import { useFormData } from '../../hooks/useFormData';
import { useFormValidation } from '../../hooks/useFormValidation';
import { hasFileFieldSizeError } from '../../helpers/fileFieldLimits';
import { appendOrderedFieldValues } from '../../helpers/fieldHelpers';
import { isFieldRenderable } from '../../helpers/resolveGenericFieldValue';
import { ProgressBar } from './components/ProgressBar';
import { FormContainer } from './components/FormContainer';
import { NavigationButtons } from './components/NavigationButtons';
import './ContactFormFrontend.scss';

const ContactFormFrontend: React.FC<ContactFormFrontendProps> = ({
  configData,
  apiUrl,
  className = ''
}) => {
  
  const [config, setConfig] = useState<ContactFormConfig | null>(null);
  const [configError, setConfigError] = useState<string>('');
  const { t } = useTranslation('form');
  const { formData, files, handleInputChange, handleFileChange, resetFormData } = useFormData();
  const { captcha, generateCaptcha, updateCaptchaResult } = useCaptcha();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaWidgetId, setRecaptchaWidgetId] = useState<number | null>(null);
  const [sessionRecaptchaConsent, setSessionRecaptchaConsent] = useState(false);


  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = useMemo(() => {
    if (!config) return 1;
    return config?.isMultiStep ? (config.steps?.length || 1) : 1;
  }, [config]);


  const { errors, clearFieldError, validateCurrentStep, validateForm, hasFileSizeErrors } = useFormValidation({
    formData,
    files,
    captcha,
    enableCaptcha: config?.enableCaptcha || false,
    captchaType: config?.captchaType || 'math',
    currentStep,
    totalSteps,
    isMultiStep: config?.isMultiStep || false,
    formFields: config?.formFields || []
  });

  
  useEffect(() => {
    if (!configData) {
      setConfigError(t('configMissing'));
      return;
    }

    try {
      const parsedConfig: ContactFormConfig = JSON.parse(configData);
      setConfig(parsedConfig);
      setConfigError('');
    } catch (e) {
      setConfigError(t('configInvalid'));
    }
  }, [configData]);

  
  useEffect(() => {
    if (config?.enableCaptcha) {
      generateCaptcha();
    }
  }, [config?.enableCaptcha, generateCaptcha]);

  useEffect(() => {
    const handleConsentUpdated = (e: Event) => {
      const customEvent = e as CustomEvent<{ recaptcha?: boolean }>;
      if (customEvent.detail?.recaptcha === true) {
        setSessionRecaptchaConsent(true);
      }
    };
    document.addEventListener('consent.updated', handleConsentUpdated);
    return () => document.removeEventListener('consent.updated', handleConsentUpdated);
  }, []);


  const handleFieldFocus = (fieldName: string) => {
    if (errors[fieldName]) {
      clearFieldError(fieldName);
    }
  };


  const evaluateCondition = (condition: any, formData: any): boolean => {
    const fieldValue = formData[condition.field] || '';
    const targetValue = condition.value || '';

    switch (condition.operator) {
      case 'equals':
        return fieldValue === targetValue;
      case 'not_equals':
        return fieldValue !== targetValue;
      case 'contains':
        return fieldValue.toString().toLowerCase().includes(targetValue.toLowerCase());
      case 'not_contains':
        return !fieldValue.toString().toLowerCase().includes(targetValue.toLowerCase());
      case 'is_empty':
        return !fieldValue || fieldValue === '';
      case 'is_not_empty':
        return fieldValue && fieldValue !== '';
      case 'greater_than':
        return parseFloat(fieldValue) > parseFloat(targetValue);
      case 'less_than':
        return parseFloat(fieldValue) < parseFloat(targetValue);
      default:
        return false;
    }
  };

  const evaluateConditionalGroup = (group: any, formData: any): boolean => {
    if (!group.conditionalLogic || !group.conditionalLogic.conditions || group.conditionalLogic.conditions.length === 0) {
      return true;
    }

    const { conditions, operator } = group.conditionalLogic;
    
    if (operator === 'and') {
      return conditions.every((condition: any) => evaluateCondition(condition, formData));
    } else {
      return conditions.some((condition: any) => evaluateCondition(condition, formData));
    }
  };

  const shouldShowField = (field: any, formData: any): boolean => {
    if (!config?.enableConditionalLogic || !config?.conditionalGroups) {
      return true; 
    }

    // Find ALL groups that contain this field
    const groupsContainingField = config.conditionalGroups.filter(group => 
      group.fields && group.fields.includes(field.id)
    );

    // If field is not in any group, show it by default
    if (groupsContainingField.length === 0) {
      return true; 
    }

    const allGroupsSatisfied = groupsContainingField.every(group => {
      const conditionMet = evaluateConditionalGroup(group, formData);
      const action = group.conditionalLogic?.action || 'show';
      return action === 'show' ? conditionMet : !conditionMet;
    });
    
    return allGroupsSatisfied;
  };

  
  const getCurrentStepFields = () => {
    let fields = config?.formFields || [];
    
  
    if (config?.isMultiStep) {
     
      fields = fields.filter(field => {
        const fieldStep = field.step === undefined || field.step === null ? 1 : parseInt(field.step.toString(), 10);
        return fieldStep === currentStep;
      });
    }
    

    fields = fields.filter(field => shouldShowField(field, formData));
    fields = fields.filter(field => isFieldRenderable(field, config?.genericFieldValues));

    return fields;
  };


  const nextStep = () => {
    const currentStepFields = getCurrentStepFields();
    
    
    const currentStepFileFields = currentStepFields.filter(field => field.type === 'file');
    const hasCurrentStepFileSizeError = currentStepFileFields.some(field => {
      const fileOrFiles = files[field.name];
      if (!fileOrFiles) {
        return false;
      }
      const fileList = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
      return hasFileFieldSizeError(field, fileList);
    });
    
    if (hasCurrentStepFileSizeError) {
      toast.error(t('fixFileSizeErrors'));
      return;
    }
    
    if (!validateCurrentStep(currentStepFields)) {
      return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const submitFormData = async (submitData: FormData) => {
    try {
      const response = await axios.post(`${apiUrl}contact-form/submit`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data?.data?.success) {
        toast.success(config?.successMessage || t('thankYouMessage'));
        resetFormData();
        if (config?.enableCaptcha) {
          if (config.captchaType === 'google_recaptcha' && recaptchaWidgetId !== null && window.grecaptcha) {
            window.grecaptcha.reset(recaptchaWidgetId);
          } else {
            generateCaptcha();
          }
        }

        if (config?.isMultiStep) {
          setCurrentStep(1);
        }
      } else {
        toast.error(response.data?.message || config?.errorMessage || 'Fehler beim Senden.');
      }
    } catch (error) {
      toast.error(config?.errorMessage || 'Fehler beim Senden.');
      if (config?.enableCaptcha) {
        if (config.captchaType === 'google_recaptcha' && recaptchaWidgetId !== null && window.grecaptcha) {
          window.grecaptcha.reset(recaptchaWidgetId);
        } else {
          generateCaptcha();
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecaptchaSubmission = (submitData: FormData) => {
    if (!recaptchaReady) {
      toast.info(t("pleaseWaitForRecaptcha"));
      setIsSubmitting(false);
      return;
    }
    const grecaptcha = typeof window !== 'undefined' ? window.grecaptcha : undefined;
    const recaptchaResponse = grecaptcha
      ? (recaptchaWidgetId !== null ? grecaptcha.getResponse(recaptchaWidgetId) : grecaptcha.getResponse())
      : '';

    if (recaptchaResponse) {
      submitData.append('g-recaptcha-response', recaptchaResponse);

      submitFormData(submitData);
    } else {
      toast.error(t("noRobotVerification"));
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (config?.isMultiStep && currentStep < totalSteps) {
      return;
    }
    
    const allVisibleFields = config?.formFields
      .filter(field => shouldShowField(field, formData))
      .filter(field => isFieldRenderable(field, config.genericFieldValues)) || [];
    
    if (hasFileSizeErrors()) {
      toast.error(t('fixFileSizeErrorsSubmit'));
      return;
    }
    
    if (!config || !validateForm(allVisibleFields)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const tokenResponse = await axios.get(`${apiUrl}contact-form/csrf-token`);
      const csrfToken = tokenResponse.data?.data?.token;
      
      if (!csrfToken) {
        throw new Error('Could not get security token');
      }

      const submitData = new FormData();
      submitData.append('jtl_token', csrfToken);
      
      appendOrderedFieldValues(
        submitData,
        config.formFields,
        formData,
        files,
        (field) => shouldShowField(field, formData) && isFieldRenderable(field, config.genericFieldValues)
      );

      if (config.formName) {
        submitData.append('form_name', config.formName);
      }
      
      submitData.append('receiver_email', config.receiverEmail || '');
      submitData.append('bcc_email', config.bccEmail || '');
      submitData.append('email_subject', config.emailSubject || t('newContactRequest'));
      submitData.append('enable_captcha', config.enableCaptcha ? '1' : '0');
      submitData.append('captcha_type', config.captchaType || 'math');
      submitData.append('success_message', config.successMessage || t('thankYouMessage'));
      submitData.append('error_message', config.errorMessage || t('errorSending'));
      
      const formFieldsJson = JSON.stringify(config.formFields);
      const formFieldsBase64 = btoa(encodeURIComponent(formFieldsJson));
      submitData.append('form_fields', formFieldsBase64);

      if (config.enableCaptcha) {
        if (config.captchaType === 'google_recaptcha') {
          const hasStoredConsent = typeof window.CM !== 'undefined' && window.CM.getSettings('recaptcha') === true;
          const hasSessionConsent = sessionRecaptchaConsent;
          if (typeof window.CM !== 'undefined' && !hasStoredConsent && !hasSessionConsent) {
            setIsSubmitting(false);
            window.CM.openConfirmationModal('recaptcha', function () {
              document.dispatchEvent(new CustomEvent('consent.updated', { detail: { recaptcha: true } }));
              if (typeof window.loadRecaptchaOnConsent === 'function') {
                window.loadRecaptchaOnConsent();
              }
            });
            return;
          }
          handleRecaptchaSubmission(submitData);
        } else {
      
          submitData.append('captcha_num1', captcha.num1.toString());
          submitData.append('captcha_num2', captcha.num2.toString());
          submitData.append('captcha_result', captcha.result);
          await submitFormData(submitData);
        }
      } else {
        await submitFormData(submitData);
      }
    } catch (error) {
      toast.error(config?.errorMessage || t('errorSending'));
      if (config?.enableCaptcha) {
        generateCaptcha();
      }
      setIsSubmitting(false);
    }
  };


  
  if (configError) {
    return (
      <div className={className} style={{ padding: '20px', color: 'red', border: '1px solid #ff6b6b', borderRadius: '4px', backgroundColor: '#fff5f5' }}>
        {configError}
      </div>
    );
  }

    
  if (!config) {
    return (
      <div className={className} style={{ padding: '20px', color: '#666' }}>
        {t('loadingContactForm')}
      </div>
    );
  }


  
  const currentStepFields = getCurrentStepFields();
  const showCaptcha = config.enableCaptcha && (!config.isMultiStep || currentStep === totalSteps);

  return (
    <div 
              className={`contact-form contactForm ${className}`}
      role="region"
      aria-label={config.formName || t('contactForm')}
    >
      <ProgressBar
        currentStep={currentStep}
        totalSteps={totalSteps}
        steps={config.steps}
        showProgress={config.isMultiStep}
        progressBarStyle={config.progressBarStyle || 'bar'}
        progressBarColor={config.progressBarColor || '#007cba'}
        progressBarBackgroundColor={config.progressBarBackgroundColor || '#e9ecef'}
        progressBarHeight={config.progressBarHeight || 8}
      />

      <form 
        onSubmit={handleSubmit} 
        className={`form ${config.formCssClass || ''}`}
        role="form"
        aria-label={config.formName || t('contactForm')}
        aria-describedby="form-description"
      >
        <div id="form-description" className="sr-only">
          {config.isMultiStep 
            ? t('multiStepForm', { totalSteps, currentStep })
            : t('contactFormForRequest')
          }
        </div>
        
        <FormContainer
          fields={currentStepFields}
          formData={formData}
          files={files}
          errors={errors}
          genericFieldValues={config.genericFieldValues}
          onInputChange={handleInputChange}
          onFileChange={handleFileChange}
          onFieldFocus={handleFieldFocus}
          showCaptcha={showCaptcha}
          captcha={captcha}
          inputCssClass={config.inputCssClass}
          onCaptchaChange={updateCaptchaResult}
          captchaType={config.captchaType || 'math'}
          apiUrl={apiUrl}
          onRecaptchaReady={setRecaptchaReady}
          onRecaptchaWidgetId={setRecaptchaWidgetId}
          sessionRecaptchaConsent={sessionRecaptchaConsent}
        />
        <NavigationButtons
          config={config}
          currentStep={currentStep}
          totalSteps={totalSteps}
          isSubmitting={isSubmitting}
          onNextStep={nextStep}
          onPrevStep={prevStep}
          onSubmit={handleSubmit}
        />
      </form>
    </div>
  );
};

export default ContactFormFrontend; 